import * as React from 'react'
import { ContentLayout } from 'components/Layouts'
// import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { BreadCrumb } from 'components/BreadCrumb/BreadCrumb'
import { DeleteAccountForm } from '../components/DeleteAccountForm/DeleteAccountForm'
import { LanguageCode } from '@cms-max/medicare-api-sdk'
import { CmsHeaderEventName } from '@cms-max/cms-browser-api-client'
import { LanguagePreference } from 'models/LanguagePreference'
import getSubdomain from 'utils/urls/getSubdomain'
import { medicareApiClient } from 'clients/medicareApiClient'
import { useMemo, useState } from 'react'
import { getUserData } from 'redux/reducers/users'
import DeleteAccountUnavailableAlert from '../components/deleteAccount/DeleteAccountUnavailableAlert'
import { useEvent } from 'hooks/useEvent'
import { DeleteAccountFormData } from 'models/forms/DeleteAccountForm'

const NoWrapSpan: React.FC = ({ children }) => {
  return <span className="my-u-whitespace-nowrap">{children}</span>
}

/**
 * Returns value to sso signout with callback uri (delete account success page)
 * @param lang
 * @returns string
 */
export const getDeleteAccountSuccessURL = (lang: LanguagePreference) => {
  const ignoredSubdomain = lang === 'es' ? 'www' : undefined
  const medicareSubdomain = getSubdomain(
    process.env.REACT_APP_MEDICARE_ENV || '',
    ignoredSubdomain
  )
  const langSubdomain = getSubdomain(lang, 'en')

  const redirectUri = `https://${
    langSubdomain + medicareSubdomain
  }medicare.gov/my/account-settings/deactivate-account-success`
  return `https://${
    langSubdomain + medicareSubdomain
  }medicare.gov/sso/signout?redirect_uri=${redirectUri}`
}

export const DeleteAccountPage = () => {
  const { t, i18n } = useTranslation('deleteAccount')
  const userData = useSelector(getUserData)
  const { fireEvent } = useEvent('accountSettings')

  const [apiError, setApiError] = useState(false)

  const elmoUnavailable = useMemo(() => {
    return Boolean(userData?.errors?.eHandbookPreferences === 'ELMO offline')
  }, [userData?.errors])

  const formProps = {
    onSubmit: async (formData: DeleteAccountFormData) => {
      setApiError(false)

      try {
        // call delete account on core
        await medicareApiClient.authenticationApi.deleteAccount({
          deleteReason: formData.reason,
          deleteTellUsMore: formData.additionalInfo,
          language: i18n.language as LanguageCode
        })

        // dispatch document event to handle clearing all cookies
        document.dispatchEvent(new CustomEvent(CmsHeaderEventName.Logout))

        // redirect to sso signout with callback uri (delete account success page)
        window.location.assign(
          getDeleteAccountSuccessURL(i18n.language as LanguagePreference)
        )
      } catch (error) {
        setApiError(true)
      }
    }, // (password: string) => setPassword(password),
    href: '/account-settings'
  }

  return (
    <>
      <BreadCrumb
        backButtonText={t('navigation.backToSettingsText')}
        width="wide"
        navAriaLabel={t('navigation.backToSettingsNavAriaLabel')}
        backButtonLink="/home"
        onClick={({ linkText, linkUrl }) =>
          fireEvent('accountsettings_deleteaccount_click', {
            linkText,
            linkUrl
          })
        }
      />
      <ContentLayout
        heading={t('heading')}
        withPrintButton={false}
        width="wide"
        className="ds-u-margin-bottom--3"
      >
        <div className="ds-l-container">
          {elmoUnavailable && (
            <DeleteAccountUnavailableAlert className="ds-u-margin-bottom--4 ds-u-measure--wide" />
          )}

          <div className="ds-u-measure--wide">
            <p>{t('content.noticeText')}</p>
            <ul>
              <li>{t('content.noticeBullet1')}</li>
              <li>{t('content.noticeBullet2')}</li>
              <li>
                <Trans
                  t={t}
                  i18nKey={'content.noticeBullet3'}
                  components={{
                    nowrap: <NoWrapSpan />
                  }}
                />
              </li>
            </ul>
            <p>
              <Trans
                t={t}
                i18nKey={'content.warningText'}
                components={{
                  nowrap: <NoWrapSpan />
                }}
              />
            </p>
          </div>

          <DeleteAccountForm {...formProps} apiError={apiError} />
        </div>
      </ContentLayout>
    </>
  )
}
